import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, TrendingUp, MessageSquare, Users, Clock, Star, Download, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import { API_URL } from '../config/api';
import { authFetch } from '../utils/auth-fetch';

interface ChatbotAnalyticsEmbedProps {
  siteId: string;
}

interface AgentInsight {
  title: string;
  content: string;
  type: 'positive' | 'negative' | 'neutral' | 'recommendation';
}

interface ImageLoadState {
  [key: string]: boolean;
}

interface OverviewData {
  overview: {
    totalConversations: number;
    totalMessages: number;
    uniqueUsers: number;
    avgDuration: number;
    avgSatisfaction: number;
    resolutionRate: number;
    avgQualityScore: number;
  };
  dailyTrends: Array<{
    date: string;
    conversations: number;
    messages: number;
    avg_duration: number;
    resolution_rate: number;
  }>;
  topIntents: Array<{
    detected_intent: string;
    frequency: number;
    avg_confidence: number;
  }>;
  contentGaps: Array<{
    question_text: string;
    question_intent: string;
    frequency_count: number;
    avg_quality_score: number;
    status: string;
  }>;
}

interface IntentData {
  intents: Array<{
    detected_intent: string;
    intent_category: string;
    frequency: number;
    avg_confidence: number;
    avg_quality_score: number;
    sample_messages: string[];
  }>;
  categories: Array<{
    intent_category: string;
    frequency: number;
    avg_confidence: number;
  }>;
  trends: Array<{
    date: string;
    detected_intent: string;
    frequency: number;
  }>;
}

interface QualityData {
  overview: {
    avgRelevance: number;
    avgHelpfulness: number;
    avgAccuracy: number;
    avgCompleteness: number;
    avgOverall: number;
    totalScoredResponses: number;
    lowQualityResponses: number;
  };
  qualityTrends: Array<{
    date: string;
    avg_quality: number;
    responses_count: number;
    low_quality_count: number;
  }>;
  lowQualityResponses: Array<{
    user_message: string;
    bot_response: string;
    overall_score: number;
    scoring_reasoning: string;
    created_at: string;
    detected_intent: string;
  }>;
}

interface FlowData {
  flows: Array<{
    flow_pattern: string;
    frequency_count: number;
    avg_duration_seconds: number;
    completion_rate: number;
    conversion_rate: number;
    abandonment_points: string[];
  }>;
  lengthDistribution: Array<{
    length_category: string;
    conversation_count: number;
    avg_duration: number;
  }>;
  abandonmentAnalysis: Array<{
    primary_intent: string;
    total_conversations: number;
    abandoned_conversations: number;
    abandonment_rate: number;
    avg_messages_before_abandonment: number;
  }>;
}

interface ContentGapData {
  contentGaps: Array<{
    id: number;
    question_text: string;
    question_intent: string;
    frequency_count: number;
    avg_quality_score: number;
    first_seen: string;
    last_seen: string;
    status: string;
    suggested_content: string;
    related_pages: string[];
  }>;
  gapTrends: Array<{
    date: string;
    new_gaps: number;
    avg_quality: number;
  }>;
  gapCategories: Array<{
    question_intent: string;
    gap_count: number;
    avg_frequency: number;
    avg_quality: number;
  }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const ChatbotAnalyticsEmbed: React.FC<ChatbotAnalyticsEmbedProps> = ({ siteId }) => {
  const [timeRange, setTimeRange] = useState('90');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  // Toggle between count and percentage display
  const [categoryDisplayMode, setCategoryDisplayMode] = useState<'count' | 'percentage'>('count');
  
  // AI Insights states
  const [intentInsights, setIntentInsights] = useState<AgentInsight[]>([]);
  const [intentInsightIndex, setIntentInsightIndex] = useState(0);
  const [intentInsightsLoading, setIntentInsightsLoading] = useState(false);
  
  const [qualityInsights, setQualityInsights] = useState<AgentInsight[]>([]);
  const [qualityInsightIndex, setQualityInsightIndex] = useState(0);
  const [qualityInsightsLoading, setQualityInsightsLoading] = useState(false);
  
  const [gapInsights, setGapInsights] = useState<AgentInsight[]>([]);
  const [gapInsightIndex, setGapInsightIndex] = useState(0);
  const [gapInsightsLoading, setGapInsightsLoading] = useState(false);
  
  // Track image load failures
  const [imageLoadErrors, setImageLoadErrors] = useState<ImageLoadState>({});
  
  // Data states
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [intentData, setIntentData] = useState<IntentData | null>(null);
  const [qualityData, setQualityData] = useState<QualityData | null>(null);
  const [flowData, setFlowData] = useState<FlowData | null>(null);
  const [contentGapData, setContentGapData] = useState<ContentGapData | null>(null);

  useEffect(() => {
    if (siteId) {
      loadData();
    }
  }, [siteId, timeRange]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadOverviewData(),
        loadIntentData(),
        loadQualityData(),
        loadFlowData(),
        loadContentGapData()
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadOverviewData = async () => {
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/overview?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load overview data');
    const data = await response.json();
    setOverviewData(data);
  };

  const loadIntentData = async () => {
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/intents?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load intent data');
    const data = await response.json();
    setIntentData(data);
    
    // Generate AI insights for intent data
    generateIntentInsights(data);
  };

  const loadQualityData = async () => {
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/quality?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load quality data');
    const data = await response.json();
    setQualityData(data);
    
    // Generate AI insights for quality data
    generateQualityInsights(data);
  };

  const loadFlowData = async () => {
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/flows?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load flow data');
    const data = await response.json();
    setFlowData(data);
  };

  const loadContentGapData = async () => {
    const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/content-gaps?days=${timeRange}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to load content gap data');
    const data = await response.json();
    setContentGapData(data);
    
    // Generate AI insights for content gap data
    generateGapInsights(data);
  };

  const exportData = async (type: string, format: string = 'json') => {
    try {
      const response = await fetch(`${API_URL}/chatbot-analytics/${siteId}/export?type=${type}&format=${format}&days=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `chatbot-analytics-${type}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Export failed');
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };
  
  const handleImageError = (imageKey: string) => {
    setImageLoadErrors(prev => ({ ...prev, [imageKey]: true }));
  };
  
  // AI Insight generation functions
  const generateIntentInsights = async (data: IntentData) => {
    setIntentInsightsLoading(true);
    setIntentInsights([]);
    setIntentInsightIndex(0);
    
    try {
      const context = {
        intents: data.intents,
        categories: data.categories,
        totalIntents: data.intents.length,
        totalCategories: data.categories.length,
        topIntent: data.intents[0],
        avgConfidence: data.intents.reduce((sum, i) => sum + i.avg_confidence, 0) / data.intents.length,
        avgQuality: data.intents.reduce((sum, i) => sum + i.avg_quality_score, 0) / data.intents.length
      };
      
      const response = await authFetch('/agent-insights/chatbot-intent', {
        method: 'POST',
        body: JSON.stringify({
          context,
          prompt: `Analyze the chatbot intent classification data and provide insights about:
1. Most common user intents and patterns
2. Intent confidence levels and accuracy
3. Category distribution and balance
4. Low confidence intents that need improvement
5. Intent evolution over time
6. Recommendations for better intent recognition`
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.insights && Array.isArray(result.insights)) {
          setIntentInsights(result.insights);
        }
      }
    } catch (error) {
      console.error('Failed to generate intent insights:', error);
    } finally {
      setIntentInsightsLoading(false);
    }
  };
  
  const generateQualityInsights = async (data: QualityData) => {
    setQualityInsightsLoading(true);
    setQualityInsights([]);
    setQualityInsightIndex(0);
    
    try {
      const context = {
        overview: data.overview,
        trends: data.qualityTrends,
        lowQualityCount: data.overview.lowQualityResponses,
        totalScored: data.overview.totalScoredResponses,
        avgScores: {
          relevance: data.overview.avgRelevance,
          helpfulness: data.overview.avgHelpfulness,
          accuracy: data.overview.avgAccuracy,
          completeness: data.overview.avgCompleteness,
          overall: data.overview.avgOverall
        }
      };
      
      const response = await authFetch('/agent-insights/chatbot-quality', {
        method: 'POST',
        body: JSON.stringify({
          context,
          prompt: `Analyze the chatbot quality scoring data and provide insights about:
1. Overall quality performance across dimensions
2. Quality trends over time
3. Specific areas needing improvement
4. Low quality response patterns
5. Comparison of quality dimensions
6. Recommendations for quality improvement`
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.insights && Array.isArray(result.insights)) {
          setQualityInsights(result.insights);
        }
      }
    } catch (error) {
      console.error('Failed to generate quality insights:', error);
    } finally {
      setQualityInsightsLoading(false);
    }
  };
  
  const generateGapInsights = async (data: ContentGapData) => {
    setGapInsightsLoading(true);
    setGapInsights([]);
    setGapInsightIndex(0);
    
    try {
      const context = {
        gaps: data.contentGaps,
        categories: data.gapCategories,
        trends: data.gapTrends,
        totalGaps: data.contentGaps.length,
        identifiedGaps: data.contentGaps.filter(g => g.status === 'identified').length,
        avgQuality: data.contentGaps.reduce((sum, g) => sum + g.avg_quality_score, 0) / data.contentGaps.length
      };
      
      const response = await authFetch('/agent-insights/chatbot-content-gap', {
        method: 'POST',
        body: JSON.stringify({
          context,
          prompt: `Analyze the content gap data and provide insights about:
1. Most critical content gaps to address
2. Common topics with missing content
3. Impact of gaps on user experience
4. Priority areas for content creation
5. Gap frequency and patterns
6. Specific content recommendations`
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.insights && Array.isArray(result.insights)) {
          setGapInsights(result.insights);
        }
      }
    } catch (error) {
      console.error('Failed to generate gap insights:', error);
    } finally {
      setGapInsightsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <span className="ml-2 text-red-500">{error}</span>
        <Button onClick={loadData} variant="outline" className="ml-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={() => exportData('overview')} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="intents">Intent Analysis</TabsTrigger>
          <TabsTrigger value="quality">Quality Scoring</TabsTrigger>
          <TabsTrigger value="flows">Conversation Flows</TabsTrigger>
          <TabsTrigger value="gaps">Content Gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {overviewData && (
            <>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Conversations</CardTitle>
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overviewData.overview.totalConversations.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      {overviewData.overview.totalMessages.toLocaleString()} total messages
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{overviewData.overview.uniqueUsers.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      {(overviewData.overview.totalMessages / overviewData.overview.uniqueUsers).toFixed(1)} messages per user
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{formatDuration(overviewData.overview.avgDuration)}</div>
                    <p className="text-xs text-muted-foreground">
                      {formatPercentage(overviewData.overview.resolutionRate)} resolution rate
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
                    <Star className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(overviewData.overview.avgQualityScore * 100).toFixed(1)}%</div>
                    <p className="text-xs text-muted-foreground">
                      {(overviewData.overview.avgSatisfaction * 100).toFixed(1)}% satisfaction
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Daily Trends Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Daily Conversation Trends</CardTitle>
                  <CardDescription>Conversations and quality metrics over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={overviewData.dailyTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="conversations" fill="#8884d8" name="Conversations" />
                      <Line yAxisId="right" type="monotone" dataKey="resolution_rate" stroke="#82ca9d" name="Resolution Rate" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Intents and Content Gaps */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Top User Intents</CardTitle>
                    <CardDescription>Most common user intentions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {overviewData.topIntents.slice(0, 5).map((intent, index) => (
                        <div key={intent.detected_intent} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                            <span className="text-sm font-medium">{intent.detected_intent}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold">{intent.frequency}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatPercentage(intent.avg_confidence)} confidence
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Content Gaps</CardTitle>
                    <CardDescription>Questions with poor response quality</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {overviewData.contentGaps.slice(0, 5).map((gap, index) => (
                        <div key={index} className="border-l-4 border-red-500 pl-4">
                          <div className="text-sm font-medium truncate">{gap.question_text}</div>
                          <div className="flex items-center justify-between mt-1">
                            <Badge variant={gap.status === 'identified' ? 'destructive' : 'secondary'}>
                              {gap.status}
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              {gap.frequency_count} occurrences · {(gap.avg_quality_score * 100).toFixed(1)}% quality
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="intents" className="space-y-6">
          {intentData && (
            <>
              {/* AI-Powered Intent Analysis */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">AI-Powered Intent Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    {imageLoadErrors['intent-classifier'] ? (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 flex-shrink-0">
                        <span className="text-blue-600 text-sm font-bold">IC</span>
                      </div>
                    ) : (
                      <img 
                        src="/crew-agents/chatbot-intent-classifier.jpg" 
                        alt="Chatbot Intent Classifier"
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        onError={() => handleImageError('intent-classifier')}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-700">David, Chatbot Intent Classifier</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = '/insights/hub/chatbot-intent-classifier'}
                        >
                          Let's talk
                        </Button>
                      </div>
                      {intentInsightsLoading ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 mb-2 flex items-center">
                            <span className="font-medium">David, Intent Classifier</span>
                            <span className="ml-2">analyzing intents</span>
                            <span className="ml-1 inline-flex">
                              <span className="animate-bounce animation-delay-0">.</span>
                              <span className="animate-bounce animation-delay-100">.</span>
                              <span className="animate-bounce animation-delay-200">.</span>
                            </span>
                          </p>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
                        </div>
                      ) : intentInsights.length > 1 ? (
                        <div className="space-y-3">
                          <div className="relative">
                            <div className="flex items-start gap-3">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                                disabled={intentInsightIndex === 0}
                                onClick={() => setIntentInsightIndex(prev => Math.max(0, prev - 1))}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              
                              <div className="flex-1 min-h-[60px]">
                                {intentInsights[intentInsightIndex] && (
                                  <>
                                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                                      {intentInsights[intentInsightIndex].title}
                                    </h4>
                                    <div 
                                      className="text-sm text-gray-600 leading-relaxed"
                                      dangerouslySetInnerHTML={{ __html: intentInsights[intentInsightIndex].content }}
                                    />
                                  </>
                                )}
                              </div>
                              
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                                disabled={intentInsightIndex === intentInsights.length - 1}
                                onClick={() => setIntentInsightIndex(prev => Math.min(intentInsights.length - 1, prev + 1))}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="flex items-center justify-center mt-3 gap-1">
                              {intentInsights.map((_, index) => (
                                <button
                                  key={index}
                                  className={`w-2 h-2 rounded-full transition-colors ${
                                    index === intentInsightIndex ? 'bg-primary' : 'bg-gray-300'
                                  }`}
                                  onClick={() => setIntentInsightIndex(index)}
                                />
                              ))}
                            </div>
                            
                            <p className="text-xs text-center text-gray-500 mt-2">
                              {intentInsightIndex + 1} of {intentInsights.length} insights
                            </p>
                          </div>
                          
                          <p className="text-xs text-gray-500 italic text-right">
                            — David, Intent Classifier
                          </p>
                        </div>
                      ) : intentInsights.length === 1 ? (
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">
                              {intentInsights[0].title}
                            </h4>
                            <div 
                              className="text-sm text-gray-600 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: intentInsights[0].content }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 italic text-right">
                            — David, Intent Classifier
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          Analyzing {intentData.intents.length} unique intents across {intentData.categories.length} categories.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Intent Categories */}
              <Card>
                <CardHeader>
                  <CardTitle>Intent Categories Distribution</CardTitle>
                  <CardDescription>
                    <div className="flex items-center justify-between">
                      <span>Breakdown of user intentions by category</span>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => setCategoryDisplayMode('count')}
                          className={`px-3 py-1 text-sm rounded ${
                            categoryDisplayMode === 'count' 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          Count
                        </button>
                        <button
                          onClick={() => setCategoryDisplayMode('percentage')}
                          className={`px-3 py-1 text-sm rounded ${
                            categoryDisplayMode === 'percentage' 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          Percentage
                        </button>
                      </div>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={intentData.categories}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ intent_category, frequency, percent }) => {
                          const displayValue = categoryDisplayMode === 'percentage' 
                            ? `${(percent * 100).toFixed(1)}%`
                            : frequency.toString();
                          return `${intent_category}: ${displayValue}`;
                        }}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="frequency"
                      >
                        {intentData.categories.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => {
                          if (categoryDisplayMode === 'percentage') {
                            const total = intentData.categories.reduce((sum, cat) => sum + cat.frequency, 0);
                            const percentage = ((value as number) / total * 100).toFixed(1);
                            return [`${percentage}%`, name];
                          }
                          return [`${value} requests`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Intent Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle>Detailed Intent Analysis</CardTitle>
                  <CardDescription>Intent frequency with quality scores and sample messages</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {intentData.intents.slice(0, 10).map((intent, index) => (
                      <div key={intent.detected_intent} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold">{intent.detected_intent}</h4>
                            <Badge variant="secondary">{intent.intent_category}</Badge>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-bold">{intent.frequency} occurrences</div>
                            <div className="text-xs text-muted-foreground">
                              {formatPercentage(intent.avg_confidence)} confidence · {(intent.avg_quality_score * 100).toFixed(1)}% quality
                            </div>
                          </div>
                        </div>
                        
                        {intent.sample_messages && intent.sample_messages.length > 0 && (
                          <div className="mt-3">
                            <h5 className="text-sm font-medium text-muted-foreground mb-2">Sample Messages:</h5>
                            <div className="space-y-1">
                              {intent.sample_messages.slice(0, 3).map((message, msgIndex) => (
                                <div key={msgIndex} className="text-sm bg-gray-50 rounded p-2 truncate">
                                  "{message}"
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="quality" className="space-y-6">
          {qualityData && (
            <>
              {/* AI-Powered Quality Analysis */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">AI-Powered Quality Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    {imageLoadErrors['quality-scorer'] ? (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-green-100 to-green-200 flex-shrink-0">
                        <span className="text-green-600 text-sm font-bold">QS</span>
                      </div>
                    ) : (
                      <img 
                        src="/crew-agents/chatbot-quality-scorer.jpg" 
                        alt="Chatbot Quality Scorer"
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        onError={() => handleImageError('quality-scorer')}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-700">Maya, Chatbot Quality Scorer</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = '/insights/hub/chatbot-quality-scorer'}
                        >
                          Let's talk
                        </Button>
                      </div>
                      {qualityInsightsLoading ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 mb-2 flex items-center">
                            <span className="font-medium">Maya, Quality Scorer</span>
                            <span className="ml-2">evaluating responses</span>
                            <span className="ml-1 inline-flex">
                              <span className="animate-bounce animation-delay-0">.</span>
                              <span className="animate-bounce animation-delay-100">.</span>
                              <span className="animate-bounce animation-delay-200">.</span>
                            </span>
                          </p>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
                        </div>
                      ) : qualityInsights.length > 1 ? (
                        <div className="space-y-3">
                          <div className="relative">
                            <div className="flex items-start gap-3">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                                disabled={qualityInsightIndex === 0}
                                onClick={() => setQualityInsightIndex(prev => Math.max(0, prev - 1))}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              
                              <div className="flex-1 min-h-[60px]">
                                {qualityInsights[qualityInsightIndex] && (
                                  <>
                                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                                      {qualityInsights[qualityInsightIndex].title}
                                    </h4>
                                    <div 
                                      className="text-sm text-gray-600 leading-relaxed"
                                      dangerouslySetInnerHTML={{ __html: qualityInsights[qualityInsightIndex].content }}
                                    />
                                  </>
                                )}
                              </div>
                              
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                                disabled={qualityInsightIndex === qualityInsights.length - 1}
                                onClick={() => setQualityInsightIndex(prev => Math.min(qualityInsights.length - 1, prev + 1))}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="flex items-center justify-center mt-3 gap-1">
                              {qualityInsights.map((_, index) => (
                                <button
                                  key={index}
                                  className={`w-2 h-2 rounded-full transition-colors ${
                                    index === qualityInsightIndex ? 'bg-primary' : 'bg-gray-300'
                                  }`}
                                  onClick={() => setQualityInsightIndex(index)}
                                />
                              ))}
                            </div>
                            
                            <p className="text-xs text-center text-gray-500 mt-2">
                              {qualityInsightIndex + 1} of {qualityInsights.length} insights
                            </p>
                          </div>
                          
                          <p className="text-xs text-gray-500 italic text-right">
                            — Maya, Quality Scorer
                          </p>
                        </div>
                      ) : qualityInsights.length === 1 ? (
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">
                              {qualityInsights[0].title}
                            </h4>
                            <div 
                              className="text-sm text-gray-600 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: qualityInsights[0].content }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 italic text-right">
                            — Maya, Quality Scorer
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          Overall quality score: {(qualityData.overview.avgOverall * 100).toFixed(1)}% with {qualityData.overview.lowQualityResponses} low quality responses.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Quality Metrics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Relevance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgRelevance * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Helpfulness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgHelpfulness * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Accuracy</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgAccuracy * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Completeness</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgCompleteness * 100).toFixed(1)}%</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Overall</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{(qualityData.overview.avgOverall * 100).toFixed(1)}%</div>
                    <p className="text-xs text-muted-foreground">
                      {qualityData.overview.lowQualityResponses} low quality
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Quality Trends */}
              <Card>
                <CardHeader>
                  <CardTitle>Quality Trends</CardTitle>
                  <CardDescription>Response quality over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={qualityData.qualityTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="avg_quality" stackId="1" stroke="#8884d8" fill="#8884d8" name="Avg Quality" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Low Quality Responses */}
              <Card>
                <CardHeader>
                  <CardTitle>Low Quality Responses</CardTitle>
                  <CardDescription>Responses that need improvement</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {qualityData.lowQualityResponses.slice(0, 5).map((response, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-red-50 border-red-200">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="destructive">Quality Score: {(response.overall_score * 100).toFixed(1)}%</Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(response.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <strong className="text-sm">User:</strong>
                            <p className="text-sm mt-1">{response.user_message}</p>
                          </div>
                          <div>
                            <strong className="text-sm">Bot:</strong>
                            <p className="text-sm mt-1">{response.bot_response}</p>
                          </div>
                          
                          {response.scoring_reasoning && (
                            <div>
                              <strong className="text-sm">Analysis:</strong>
                              <p className="text-xs text-muted-foreground mt-1">{response.scoring_reasoning}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="flows" className="space-y-6">
          {flowData && (
            <>
              {/* Conversation Length Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Conversation Length Distribution</CardTitle>
                  <CardDescription>How long conversations typically last</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={flowData.lengthDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="length_category" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="conversation_count" fill="#8884d8" name="Conversations" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top Conversation Flows */}
              <Card>
                <CardHeader>
                  <CardTitle>Most Common Conversation Flows</CardTitle>
                  <CardDescription>Intent sequences and completion rates</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {flowData.flows.slice(0, 10).map((flow, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">{flow.flow_pattern}</code>
                          <Badge variant="secondary">{flow.frequency_count} times</Badge>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Duration:</span>
                            <div className="font-medium">{formatDuration(flow.avg_duration_seconds)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Completion:</span>
                            <div className="font-medium">{formatPercentage(flow.completion_rate)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Conversion:</span>
                            <div className="font-medium">{formatPercentage(flow.conversion_rate)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Abandonment Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle>Abandonment Analysis</CardTitle>
                  <CardDescription>Where users typically drop off</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {flowData.abandonmentAnalysis.slice(0, 10).map((analysis, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold">{analysis.primary_intent}</h4>
                          <Badge variant={analysis.abandonment_rate > 0.5 ? 'destructive' : 'secondary'}>
                            {formatPercentage(analysis.abandonment_rate)} abandonment
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Total Conversations:</span>
                            <div className="font-medium">{analysis.total_conversations}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Avg Messages Before Abandonment:</span>
                            <div className="font-medium">{analysis.avg_messages_before_abandonment ? Number(analysis.avg_messages_before_abandonment).toFixed(1) : 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="gaps" className="space-y-6">
          {contentGapData && (
            <>
              {/* AI-Powered Content Gap Analysis */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">AI-Powered Content Gap Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-4">
                    {imageLoadErrors['content-gap'] ? (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200 flex-shrink-0">
                        <span className="text-red-600 text-sm font-bold">CG</span>
                      </div>
                    ) : (
                      <img 
                        src="/crew-agents/chatbot-content-gap-detector.jpg" 
                        alt="Content Gap Detector"
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        onError={() => handleImageError('content-gap')}
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-700">Oliver, Content Gap Detector</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = '/insights/hub/chatbot-content-gap-detector'}
                        >
                          Let's talk
                        </Button>
                      </div>
                      {gapInsightsLoading ? (
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600 mb-2 flex items-center">
                            <span className="font-medium">Gap Detector</span>
                            <span className="ml-2">analyzing gaps</span>
                            <span className="ml-1 inline-flex">
                              <span className="animate-bounce animation-delay-0">.</span>
                              <span className="animate-bounce animation-delay-100">.</span>
                              <span className="animate-bounce animation-delay-200">.</span>
                            </span>
                          </p>
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
                        </div>
                      ) : gapInsights.length > 1 ? (
                        <div className="space-y-3">
                          <div className="relative">
                            <div className="flex items-start gap-3">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                                disabled={gapInsightIndex === 0}
                                onClick={() => setGapInsightIndex(prev => Math.max(0, prev - 1))}
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              
                              <div className="flex-1 min-h-[60px]">
                                {gapInsights[gapInsightIndex] && (
                                  <>
                                    <h4 className="text-sm font-medium text-gray-700 mb-1">
                                      {gapInsights[gapInsightIndex].title}
                                    </h4>
                                    <div 
                                      className="text-sm text-gray-600 leading-relaxed"
                                      dangerouslySetInnerHTML={{ __html: gapInsights[gapInsightIndex].content }}
                                    />
                                  </>
                                )}
                              </div>
                              
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                                disabled={gapInsightIndex === gapInsights.length - 1}
                                onClick={() => setGapInsightIndex(prev => Math.min(gapInsights.length - 1, prev + 1))}
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="flex items-center justify-center mt-3 gap-1">
                              {gapInsights.map((_, index) => (
                                <button
                                  key={index}
                                  className={`w-2 h-2 rounded-full transition-colors ${
                                    index === gapInsightIndex ? 'bg-primary' : 'bg-gray-300'
                                  }`}
                                  onClick={() => setGapInsightIndex(index)}
                                />
                              ))}
                            </div>
                            
                            <p className="text-xs text-center text-gray-500 mt-2">
                              {gapInsightIndex + 1} of {gapInsights.length} insights
                            </p>
                          </div>
                          
                          <p className="text-xs text-gray-500 italic text-right">
                            — Oliver, Gap Detector
                          </p>
                        </div>
                      ) : gapInsights.length === 1 ? (
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-1">
                              {gapInsights[0].title}
                            </h4>
                            <div 
                              className="text-sm text-gray-600 leading-relaxed"
                              dangerouslySetInnerHTML={{ __html: gapInsights[0].content }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 italic text-right">
                            — Oliver, Gap Detector
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">
                          Found {contentGapData.contentGaps.length} content gaps across {contentGapData.gapCategories.length} categories.
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Gap Categories */}
              <Card>
                <CardHeader>
                  <CardTitle>Content Gap Categories</CardTitle>
                  <CardDescription>Types of questions that need better answers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {contentGapData.gapCategories.map((category, index) => (
                      <div key={category.question_intent} className="border rounded-lg p-4">
                        <h4 className="font-semibold mb-2">{category.question_intent}</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Gaps:</span>
                            <span className="font-medium">{category.gap_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Avg Frequency:</span>
                            <span className="font-medium">{Number(category.avg_frequency).toFixed(1)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Avg Quality:</span>
                            <span className="font-medium">{(Number(category.avg_quality) * 100).toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Content Gaps */}
              <Card>
                <CardHeader>
                  <CardTitle>Identified Content Gaps</CardTitle>
                  <CardDescription>Questions that need attention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contentGapData.contentGaps.slice(0, 10).map((gap) => (
                      <div key={gap.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant={
                            gap.status === 'identified' ? 'destructive' :
                            gap.status === 'addressed' ? 'default' : 'secondary'
                          }>
                            {gap.status}
                          </Badge>
                          <div className="text-sm text-muted-foreground">
                            {gap.frequency_count} occurrences · {(gap.avg_quality_score * 100).toFixed(1)}% quality
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div>
                            <strong className="text-sm">Question:</strong>
                            <p className="text-sm mt-1">{gap.question_text}</p>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                            <span>Intent: {gap.question_intent}</span>
                            <span>First seen: {new Date(gap.first_seen).toLocaleDateString()}</span>
                            <span>Last seen: {new Date(gap.last_seen).toLocaleDateString()}</span>
                          </div>
                          
                          {gap.suggested_content && (
                            <div>
                              <strong className="text-sm">Suggestion:</strong>
                              <p className="text-sm mt-1 text-muted-foreground">{gap.suggested_content}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ChatbotAnalyticsEmbed;